import { ConfirmationModal } from "@platform/design-system";
import React from "react";

/** Which requote resolution the dialog confirms (ADR-031). */
export type RequoteAction = "replace" | "disregard" | "promote" | "restore";

interface RequoteActionDialogProps {
  /** The action to confirm, or `null` when the dialog is closed. */
  action: RequoteAction | null;
  onConfirm: () => void;
  onCancel: () => void;
}

// "promote" (a historical column) and "replace" (a pending column) both
// promote the target to active, so they share this copy (CD-89 AC7).
const REPLACE_ACTIVE_QUOTE_COPY = {
  title: "Replace Quote?",
  description:
    "Are you sure you want to replace the currently active quote with this quote? The currently active quote will be archived and viewable in Quote History.",
};

const COPY: Record<RequoteAction, { title: string; description: string }> = {
  replace: REPLACE_ACTIVE_QUOTE_COPY,
  disregard: {
    title: "Disregard Quote?",
    description:
      "Are you sure you want to disregard this quote? You can access it in the Quote History later.",
  },
  promote: REPLACE_ACTIVE_QUOTE_COPY,
  // Restoring never promotes, so this deliberately does not promise the quote will be
  // active again — a Replace during the deletion leaves it in Quote History, and the
  // success toast says which happened.
  restore: {
    title: "Restore Quote?",
    description:
      "Are you sure you want to restore this quote? It will be returned to the side-by-side unless a newer quote has since replaced it, in which case it stays in Quote History.",
  },
};

/**
 * Presentational confirmation dialog for the requote actions — Replace /
 * Disregard on a pending column, Replace the Active Quote on a historical one
 * (CD-89 AC7, "promote" internally — same promote-to-active mutation as
 * Replace). The action itself is owned by the parent (the SBS carrier header)
 * so the mutation's success handler survives this dialog closing.
 */
export const RequoteActionDialog: React.FC<RequoteActionDialogProps> = ({
  action,
  onConfirm,
  onCancel,
}) => {
  if (!action) return null;

  return (
    <ConfirmationModal
      open
      onCancel={onCancel}
      onAccept={onConfirm}
      title={COPY[action].title}
      description={COPY[action].description}
      confirmButtonText="Confirm"
      cancelButtonText="Cancel"
      CancelButtonProps={{ variant: "tertiary" }}
    />
  );
};
